import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Package, ShoppingBag, User, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/constants";

const NAV = [
  { to: "/bayi", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/bayi/urunler", icon: Package, label: "Ürünler" },
  { to: "/bayi/siparislerim", icon: ShoppingBag, label: "Siparişlerim" },
  { to: "/bayi/profil", icon: User, label: "Firma Bilgilerim" },
];

export default function DealerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { document.title = "Bayi Paneli – Evser Halı"; }, []);

  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const SidebarBody = () => (
    <>
      <div className="p-6 border-b border-[#2a2a2a] flex items-center justify-between">
        <Link to="/bayi" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-auto bg-white p-1" />
          <div>
            <div className="font-serif text-lg leading-none">Evser Halı</div>
            <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#c8a97e] mt-1">Bayi Paneli</div>
          </div>
        </Link>
        <button className="lg:hidden text-[#f7f5f0]/70 hover:text-[#f7f5f0] p-1" onClick={() => setMenuOpen(false)} aria-label="Kapat">
          <X size={20} strokeWidth={1.5} />
        </button>
      </div>
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`dealer-nav-${item.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 my-1 text-sm transition-colors ${
                isActive ? "bg-[#c8a97e] text-[#1a1a1a]" : "text-[#d4cfc4] hover:bg-[#1f1f1f]"
              }`
            }
          >
            <item.icon size={16} strokeWidth={1.5} /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#2a2a2a]">
        <div className="px-4 py-3 text-xs text-[#a09a8e] border-b border-[#222] mb-2">
          <div className="text-[#f7f5f0] font-medium truncate">{user?.company?.short_name || user?.full_name}</div>
          <div className="mt-0.5 truncate">{user?.username}</div>
        </div>
        <button
          onClick={() => { logout(); nav("/bayi-girisi"); }}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#d4cfc4] hover:bg-[#1f1f1f]"
          data-testid="dealer-logout-btn"
        >
          <LogOut size={16} strokeWidth={1.5} /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f7f5f0]">
      <aside
        className="hidden lg:flex w-64 bg-[#121212] text-[#f7f5f0] flex-col fixed inset-y-0 left-0 z-40"
        data-testid="dealer-sidebar"
      >
        <SidebarBody />
      </aside>

      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
        <aside
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#121212] text-[#f7f5f0] flex flex-col transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="dealer-mobile-sidebar"
        >
          <SidebarBody />
        </aside>
      </div>

      <div className="lg:ml-64 flex flex-col min-h-screen">
        <header className="lg:hidden bg-white border-b border-[#eae5dc] px-4 h-16 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="p-2 -ml-2 hover:bg-[#f7f5f0]"
              onClick={() => setMenuOpen(true)}
              aria-label="Menüyü aç"
              data-testid="dealer-mobile-menu-btn"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <Link to="/bayi" className="flex items-center gap-2 min-w-0">
              <img src={LOGO_URL} alt="" className="h-8 w-auto" />
              <div className="font-serif text-base truncate">Bayi Paneli</div>
            </Link>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden" data-testid="dealer-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
